/**
 * Status Indicator Widget
 * Displays a large status indicator for dashboard use
 */

import {
  Check,
  Clock,
  AlertTriangle,
  AlertCircle,
  Package,
  Play,
  Pause,
  Minus,
  TrendingUp,
  TrendingDown,
  Activity,
  LucideIcon,
} from 'lucide-react';

// Icon mapping
const ICON_MAP: Record<string, LucideIcon> = {
  check: Check,
  clock: Clock,
  'alert-triangle': AlertTriangle,
  'alert-circle': AlertCircle,
  package: Package,
  play: Play,
  pause: Pause,
  minus: Minus,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  activity: Activity,
};

// Default status colors
const STATUS_COLORS: Record<string, string> = {
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  neutral: '#6b7280',
};

interface StatusIndicatorWidgetProps {
  value?: string | number;
  label?: string;
  status?: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  icon?: string;
  color?: string;
  subtitle?: string;
  loading?: boolean;
}

export function StatusIndicatorWidget({
  value,
  label = 'Status',
  status = 'neutral',
  icon = 'activity',
  color,
  subtitle,
  loading = false,
}: StatusIndicatorWidgetProps) {
  const IconComponent = ICON_MAP[icon] || Activity;
  const statusColor = color || STATUS_COLORS[status] || STATUS_COLORS.neutral;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 animate-pulse">
        <div className="w-16 h-16 rounded-full bg-gray-200 mb-4" />
        <div className="h-8 w-24 bg-gray-200 rounded mb-2" />
        <div className="h-4 w-32 bg-gray-100 rounded" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full p-6">
      {/* Icon Circle */}
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
        style={{ backgroundColor: `${statusColor}20` }}
      >
        <IconComponent
          className="w-10 h-10"
          style={{ color: statusColor }}
        />
      </div>

      {/* Value */}
      <div
        className="text-3xl font-bold mb-1"
        style={{ color: statusColor }}
      >
        {value ?? '-'}
      </div>

      {/* Label */}
      <div className="text-gray-600 font-medium text-center">
        {label}
      </div>

      {/* Subtitle */}
      {subtitle && (
        <div className="text-sm text-gray-400 mt-1 text-center">
          {subtitle}
        </div>
      )}
    </div>
  );
}
